import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { listWidgets, getWidget, VALID_CATEGORIES } from './widgetRegistry';
import LayoutRenderer from './LayoutRenderer';
import useConfirm from './useConfirm';
import ScriptEditor from './scriptEditor';
import ScriptEditorWithPreview from './scriptPreviewPanel';
import useDesignerHistory from './useDesignerHistory';
import './widgets/index'; // ensure widgets are registered when the designer mounts standalone

// Designer for a single Layout. Three-pane (palette / canvas / inspector) +
// PublishBar + Bindings panel + History tab.
//
// Concurrency model: pessimistic lock claimed on mount, heartbeat every
// 10 minutes, released on unmount. Second editor sees a "locked by …" banner
// and the canvas in read-only mode. Workspace admin can force-release.
//
// Auto-save: debounced PATCH every ~10 seconds when the draft changes.
// PATCH writes only to draft fields. Publish is an explicit user action.

const LOCK_HEARTBEAT_MS = 10 * 60 * 1000;
const AUTOSAVE_DEBOUNCE_MS = 10 * 1000;

const styles = {
  shell:        { display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' },
  topbar:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0, gap: '12px' },
  publishBar:   { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0, fontSize: '12px' },
  body:         { flex: 1, display: 'grid', gridTemplateColumns: '220px 1fr 320px', gridTemplateRows: '1fr', minHeight: 0 },
  pane:         { borderRight: '1px solid var(--border-color)', overflow: 'auto', background: 'var(--bg-secondary)' },
  paneRight:    { borderLeft: '1px solid var(--border-color)', overflow: 'auto', background: 'var(--bg-secondary)' },
  paneCenter:   { background: 'var(--bg-primary)', overflow: 'auto', padding: '14px' },
  paneTitle:    { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', padding: '10px 12px', letterSpacing: '0.05em', borderBottom: '1px solid var(--border-color)' },
  btnPrimary:   { background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' },
  btnSecondary: { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' },
  btnDanger:    { background: '#dc262620', color: '#ef4444', border: '1px solid #ef444450', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
  input:        { padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  banner: (color) => ({ padding: '8px 14px', background: `${color}20`, color, fontSize: '12px', borderBottom: `1px solid ${color}40`, flexShrink: 0 }),
};

function authHeaders(token) { return { Authorization: `Bearer ${token}` }; }

// Build a snapshot of {tree, events, scripts} that auto-save can compare
// against to skip no-op PATCHes.
function makeSig(tree, events, scripts) {
  return JSON.stringify({ t: tree, e: events, s: scripts });
}

// Mutators on the in-memory tree — return a *new* tree so React state diffs
// reliably. All operate on the immutable BUILTIN_*-style shape.
function findAndUpdate(tree, id, mutator) {
  if (!tree) return tree;
  if (tree.id === id) return mutator({ ...tree });
  if (Array.isArray(tree.children)) {
    let changed = false;
    const nextChildren = tree.children.map(c => {
      const updated = findAndUpdate(c, id, mutator);
      if (updated !== c) changed = true;
      return updated;
    });
    if (changed) return { ...tree, children: nextChildren };
  }
  return tree;
}

function findAndRemove(tree, id) {
  if (!tree || tree.id === id) return null;
  if (Array.isArray(tree.children)) {
    const nextChildren = [];
    let changed = false;
    for (const c of tree.children) {
      const r = findAndRemove(c, id);
      if (r === c) nextChildren.push(c);
      else if (r === null) { changed = true; }
      else { nextChildren.push(r); changed = true; }
    }
    if (changed) return { ...tree, children: nextChildren };
  }
  return tree;
}

function findAndAddChild(tree, parentId, child) {
  if (!tree) return tree;
  if (tree.id === parentId) {
    return { ...tree, children: [...(tree.children || []), child] };
  }
  if (Array.isArray(tree.children)) {
    let changed = false;
    const nextChildren = tree.children.map(c => {
      const u = findAndAddChild(c, parentId, child);
      if (u !== c) changed = true;
      return u;
    });
    if (changed) return { ...tree, children: nextChildren };
  }
  return tree;
}

function findById(tree, id) {
  if (!tree) return null;
  if (tree.id === id) return tree;
  if (Array.isArray(tree.children)) {
    for (const c of tree.children) {
      const r = findById(c, id);
      if (r) return r;
    }
  }
  return null;
}

function findParent(tree, id) {
  if (!tree || !Array.isArray(tree.children)) return null;
  for (const c of tree.children) {
    if (c.id === id) return tree;
    const r = findParent(c, id);
    if (r) return r;
  }
  return null;
}

function findChildIndex(parent, childId) {
  if (!parent || !Array.isArray(parent.children)) return -1;
  return parent.children.findIndex(c => c.id === childId);
}

// True iff `id` is `node` itself or anywhere in its subtree. Used to block
// dropping a container into its own descendant (would create a cycle).
function subtreeContains(node, id) {
  if (!node) return false;
  if (node.id === id) return true;
  if (Array.isArray(node.children)) {
    for (const c of node.children) {
      if (subtreeContains(c, id)) return true;
    }
  }
  return false;
}

// Insert `child` at index `idx` under `parentId`. Pass `idx = -1` (or out of
// range) to append. Returns a new tree; identity-preserving when nothing
// matched so React can skip re-renders cheaply.
function findAndInsertAt(tree, parentId, child, idx) {
  if (!tree) return tree;
  if (tree.id === parentId) {
    const kids = Array.isArray(tree.children) ? tree.children.slice() : [];
    const at = (idx == null || idx < 0 || idx > kids.length) ? kids.length : idx;
    kids.splice(at, 0, child);
    return { ...tree, children: kids };
  }
  if (Array.isArray(tree.children)) {
    let changed = false;
    const nextChildren = tree.children.map(c => {
      const u = findAndInsertAt(c, parentId, child, idx);
      if (u !== c) changed = true;
      return u;
    });
    if (changed) return { ...tree, children: nextChildren };
  }
  return tree;
}

// Atomic move: remove the node identified by `sourceId`, then insert it under
// `targetParentId` at `targetIdx`. Cycle-safe (target inside source = no-op),
// no-op when source == target parent and the resulting index would be
// unchanged. Returns the new tree (or original on no-op / invalid).
function findAndMove(tree, sourceId, targetParentId, targetIdx) {
  if (!tree || !sourceId || !targetParentId) return tree;
  if (sourceId === targetParentId) return tree; // can't drop into self
  const source = findById(tree, sourceId);
  if (!source) return tree;
  if (subtreeContains(source, targetParentId)) return tree; // cycle
  const oldParent = findParent(tree, sourceId);
  if (!oldParent) return tree; // root can't be moved
  const oldIdx = findChildIndex(oldParent, sourceId);
  // Adjust target index if removing source from the same parent shifts later
  // siblings down by one.
  let adjIdx = targetIdx;
  if (oldParent.id === targetParentId && targetIdx != null && targetIdx > oldIdx) {
    adjIdx = targetIdx - 1;
  }
  // No-op when the move would land in the same slot.
  if (oldParent.id === targetParentId && (adjIdx === oldIdx || (adjIdx == null && oldIdx === oldParent.children.length - 1))) {
    return tree;
  }
  const without = findAndRemove(tree, sourceId);
  if (!without) return tree;
  return findAndInsertAt(without, targetParentId, source, adjIdx);
}

function genId(type) {
  return `${type.replace('.', '_')}_${Math.random().toString(36).slice(2, 8)}`;
}


export default function LayoutDesigner({ token, workspaceId, currentUser, layoutId, onBack }) {
  const [layout, setLayout] = useState(null);
  const history = useDesignerHistory({ tree: null, events: [] });
  const { tree, events, setTree, setEvents, undo, redo, reset: resetHistory, canUndo, canRedo } = history;
  const [scripts, setScripts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('canvas'); // canvas | bindings | history
  const [error, setError] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved | error
  const [lockState, setLockState] = useState({ locked: false, holder: null, holderName: null, expires: null, isMe: false });
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showWiresDialog, setShowWiresDialog] = useState(false);
  const [previewMode, setPreviewMode] = useState('edit'); // 'edit' | 'mobile' | 'tablet' | 'desktop'
  // Drag/drop state. dragRef holds {kind: 'palette', widgetType} or
  // {kind: 'move', id}; isDragging gates the visibility of drop indicators
  // (no point showing them when the user isn't dragging anything).
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  // Sig of last saved draft — autosave skips when the live state matches.
  const savedSig = useRef('');

  // ------- Load -------
  const loadLayout = useCallback(async () => {
    setError('');
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}`,
        { headers: authHeaders(token) },
      );
      const l = r.data;
      setLayout(l);
      resetHistory({ tree: l.draft.tree_json, events: l.draft.events_json || [] });
      setScripts(l.draft.scripts_json || []);
      savedSig.current = makeSig(l.draft.tree_json, l.draft.events_json || [], l.draft.scripts_json || []);
      // Treat an expired lock as unlocked. The atomic UPDATE in lock-claim
      // already overrides expired holders server-side, but the GET serializer
      // returns whatever's in the DB — including stale rows from a session
      // that crashed before releasing. Without this guard the UI shows
      // "Locked by X · expires at <past>" forever and never auto-claims.
      const expired = l.lock_expires_at && new Date(l.lock_expires_at) <= new Date();
      const liveHolder = !expired ? l.locked_by_user_id : null;
      const isMe = liveHolder != null && liveHolder === currentUser?.id;
      setLockState({
        locked: liveHolder != null,
        holder: liveHolder,
        holderName: !expired ? (l.locked_by_user_name || null) : null,
        expires: l.lock_expires_at,
        isMe,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load layout.');
    }
  }, [token, workspaceId, layoutId, currentUser?.id]);

  useEffect(() => { loadLayout(); }, [loadLayout]);

  // ------- Lock claim + heartbeat -------
  const claimLock = useCallback(async () => {
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/lock-claim`,
        {}, { headers: authHeaders(token) },
      );
      setLockState({
        locked: true,
        holder: r.data.locked_by_user_id,
        holderName: r.data.locked_by_user_name || null,
        expires: r.data.lock_expires_at,
        isMe: r.data.locked_by_user_id === currentUser?.id,
      });
      setError('');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === 'layout_locked') {
        setLockState({
          locked: true,
          holder: detail.locked_by_user_id,
          holderName: detail.locked_by_user_name || null,
          expires: detail.lock_expires_at,
          isMe: false,
        });
      } else {
        setError(detail?.message || detail || 'Failed to claim lock');
      }
    }
  }, [token, workspaceId, layoutId, currentUser?.id]);

  // Auto-claim once on first mount, then re-claim if the lock state shows
  // we lost it (e.g. another admin force-released).
  useEffect(() => {
    if (!layout) return;
    if (!lockState.locked || (!lockState.isMe && lockState.holder == null)) {
      claimLock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout?.id]);

  useEffect(() => {
    if (!lockState.isMe) return;
    const t = setInterval(async () => {
      try {
        await axios.post(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/lock-extend`,
          {}, { headers: authHeaders(token) },
        );
      } catch (e) {
        // Lost the lock — refresh state.
        loadLayout();
      }
    }, LOCK_HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [lockState.isMe, token, workspaceId, layoutId, loadLayout]);

  // Best-effort release on unmount — the server expires stale locks via TTL,
  // so a missed release is recoverable, but releasing eagerly avoids the
  // "I closed the tab; now nobody can edit for 30 minutes" complaint.
  useEffect(() => {
    return () => {
      if (lockState.isMe) {
        axios.post(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/lock-release`,
          {}, { headers: authHeaders(token) },
        ).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockState.isMe]);

  // Manual draft save — used by the Save button and Ctrl/⌘+S. Returns true on
  // success so callers (publish, etc.) can chain. No-op when there's nothing
  // dirty; the auto-save effect short-circuits on the same sig check, so
  // calling doSave is safe to spam.
  const doSave = useCallback(async () => {
    if (!layout || !lockState.isMe) return false;
    const sig = makeSig(tree, events, scripts);
    if (sig === savedSig.current) {
      setSaveState('saved');
      return true;
    }
    setSaveState('saving');
    try {
      await axios.patch(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}`,
        { draft_tree_json: tree, draft_events_json: events, draft_scripts_json: scripts },
        { headers: authHeaders(token) },
      );
      savedSig.current = sig;
      setSaveState('saved');
      return true;
    } catch (e) {
      setSaveState('error');
      setError(e?.response?.data?.detail || 'Save failed');
      return false;
    }
  }, [layout, lockState.isMe, tree, events, scripts, token, workspaceId, layoutId]);

  // ------- Auto-save (debounced PATCH) -------
  useEffect(() => {
    if (!layout || !lockState.isMe) return;
    const sig = makeSig(tree, events, scripts);
    if (sig === savedSig.current) {
      setSaveState('saved');
      return;
    }
    setSaveState('dirty');
    const t = setTimeout(() => { doSave(); }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [tree, events, scripts, layout?.id, lockState.isMe, doSave]);

  // ------- Tree mutators -------
  // Insert a fresh widget under `parentId`. `idx` is optional — omit/null to
  // append. Used by the palette (click-to-add → append; drop-on-zone → insert
  // at the zone's index).
  const addWidget = useCallback((parentId, widgetType, idx) => {
    const meta = getWidget(widgetType);
    if (!meta) return;
    const node = {
      id: genId(widgetType),
      type: widgetType,
      config: {},
      children: meta.isContainer ? [] : [],
    };
    if (idx == null) {
      setTree(prev => findAndAddChild(prev, parentId, node));
    } else {
      setTree(prev => findAndInsertAt(prev, parentId, node, idx));
    }
    setSelectedId(node.id);
  }, [setTree]);

  // Move an existing canvas node under `targetParentId` at `targetIdx`. No-op
  // when target is the source's own subtree (cycle-safe via findAndMove).
  const moveWidget = useCallback((sourceId, targetParentId, targetIdx) => {
    if (!sourceId || !targetParentId) return;
    if (sourceId === targetParentId) return;
    setTree(prev => findAndMove(prev, sourceId, targetParentId, targetIdx));
  }, [setTree]);

  const updateConfig = useCallback((id, patch) => {
    // Coalesce successive edits to the same field on the same widget so
    // typing into a textarea doesn't burn through the 50-entry history cap
    // in two seconds. Different fields → different keys → separate entries.
    const fields = patch && typeof patch === 'object' ? Object.keys(patch).sort().join(',') : '';
    const coalesceKey = `config:${id}:${fields}`;
    setTree(prev => findAndUpdate(prev, id, n => ({ ...n, config: { ...(n.config || {}), ...patch } })), coalesceKey);
  }, [setTree]);

  const removeWidget = useCallback((id) => {
    if (!tree) return;
    if (tree.id === id) return; // can't delete the root
    setTree(prev => findAndRemove(prev, id) || prev);
    setSelectedId(null);
  }, [tree, setTree]);

  // Keyboard nav (Phase L6 §6):
  //   Ctrl/⌘ + Z      = undo
  //   Ctrl/⌘ + ⇧ + Z  = redo (also Ctrl + Y)
  //   Arrow Down/Up   = move selection through flattened tree (next/prev)
  //   Arrow Right     = jump into first child (containers only)
  //   Arrow Left      = jump out to parent
  //   Delete / Backspace = remove selected widget (root protected)
  // All gated on a text-input check so typing in config fields still gets the
  // native input behaviour (browser's own undo/cursor movement).
  useEffect(() => {
    if (!lockState.isMe || !tree) return;
    const onKey = (e) => {
      const t = e.target;
      const tag = t && t.tagName;
      const isField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable);
      if (isField) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod) {
        const key = e.key.toLowerCase();
        if (key === 's') { e.preventDefault(); doSave(); return; }
        if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
        if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); return; }
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const flat = flattenTree(tree);
        if (flat.length === 0) return;
        const idx = selectedId ? flat.findIndex(n => n.id === selectedId) : -1;
        const nextIdx = e.key === 'ArrowDown'
          ? (idx < 0 ? 0 : Math.min(idx + 1, flat.length - 1))
          : (idx <= 0 ? 0 : idx - 1);
        setSelectedId(flat[nextIdx].id);
      } else if (e.key === 'ArrowRight') {
        if (!selectedId) return;
        const node = findById(tree, selectedId);
        if (node && Array.isArray(node.children) && node.children.length > 0) {
          e.preventDefault();
          setSelectedId(node.children[0].id);
        }
      } else if (e.key === 'ArrowLeft') {
        if (!selectedId) return;
        const parent = findParent(tree, selectedId);
        if (parent) { e.preventDefault(); setSelectedId(parent.id); }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId && tree.id !== selectedId) {
          e.preventDefault();
          removeWidget(selectedId);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lockState.isMe, tree, selectedId, undo, redo, removeWidget, doSave]);

  // Wrap the current root in a transparent container.stack so children added
  // through the palette become visible. Only relevant for legacy layouts
  // seeded as flat *.builtin trees before the wrap was applied server-side.
  const wrapRootInStack = useCallback(() => {
    setTree(prev => {
      if (!prev) return prev;
      const inner = { ...prev, id: prev.id === 'root' ? `default-${prev.type.replace(/\./g, '-')}` : prev.id };
      return {
        id: 'root',
        type: 'container.stack',
        config: { padding: false, gap: 0 },
        children: [inner],
      };
    });
    setSelectedId('root');
  }, [setTree]);

  const rootIsLeaf = !!(tree && !getWidget(tree.type)?.isContainer);

  // ------- Publish -------
  const doPublish = useCallback(async (changeNote) => {
    setSaveState('saving');
    try {
      // Force-flush any pending auto-save first.
      const sig = makeSig(tree, events, scripts);
      if (sig !== savedSig.current) {
        await axios.patch(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}`,
          { draft_tree_json: tree, draft_events_json: events, draft_scripts_json: scripts },
          { headers: authHeaders(token) },
        );
        savedSig.current = sig;
      }
      await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/publish`,
        { change_note: changeNote || null },
        { headers: authHeaders(token) },
      );
      setSaveState('saved');
      setShowPublishDialog(false);
      // Publish = "I'm done with this edit session." Release the lock
      // *synchronously* before navigating back so the layouts list reload
      // doesn't race with the unmount-time release and show a stale
      // "🔒 user X" row. Locally clear isMe so the unmount cleanup skips
      // its (now redundant) release POST.
      try {
        await axios.post(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/lock-release`,
          {}, { headers: authHeaders(token) },
        );
      } catch (_) { /* best-effort — TTL will expire it eventually */ }
      setLockState(prev => ({ ...prev, locked: false, holder: null, holderName: null, isMe: false }));
      if (onBack) onBack();
    } catch (e) {
      setSaveState('error');
      setError(e?.response?.data?.detail || 'Publish failed');
    }
  }, [tree, events, scripts, token, workspaceId, layoutId, onBack]);

  const doDiscardDraft = useCallback(async () => {
    const ok = await confirm({
      title: 'Discard draft?',
      message: 'All unpublished changes will be replaced with the currently-published version. This cannot be undone.',
      confirmLabel: 'Discard',
      danger: true,
    });
    if (!ok) return;
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/discard-draft`,
        {}, { headers: authHeaders(token) },
      );
      setLayout(r.data);
      resetHistory({ tree: r.data.draft.tree_json, events: r.data.draft.events_json || [] });
      setScripts(r.data.draft.scripts_json || []);
      savedSig.current = makeSig(r.data.draft.tree_json, r.data.draft.events_json || [], r.data.draft.scripts_json || []);
      setSaveState('saved');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Discard failed');
    }
  }, [token, workspaceId, layoutId, confirm]);

  // ------- Render -------
  if (!layout) {
    return (
      <div style={styles.shell}>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          {error || 'Loading layout…'}
        </div>
        {onBack && <button onClick={onBack} style={{ ...styles.btnSecondary, margin: '12px auto' }}>← Back to layouts</button>}
      </div>
    );
  }

  const selected = selectedId ? findById(tree, selectedId) : null;
  const readOnly = !lockState.isMe;
  const hasUnpublished = layout.has_unpublished_changes;

  return (
    <div style={styles.shell}>
      {/* Top bar */}
      <div style={styles.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {onBack && <button onClick={onBack} style={styles.btnSecondary}>← Layouts</button>}
          <strong style={{ fontSize: '14px' }}>{layout.name}</strong>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {layout.category}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
          {[
            ['canvas', 'Canvas'],
            ['bindings', 'Bindings'],
            ['history', 'History'],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                ...styles.btnSecondary,
                background: tab === id ? 'var(--accent-color)' : 'var(--bg-tertiary)',
                color: tab === id ? '#fff' : 'var(--text-primary)',
                fontWeight: tab === id ? 600 : 400,
              }}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Lock banner */}
      {readOnly && lockState.locked && (
        <div style={styles.banner('#f59e0b')}>
          🔒 Layout is locked by {lockState.holderName || `user ${lockState.holder}`}
          {lockState.expires ? ` until ${new Date(lockState.expires).toLocaleString()}` : ''}. You can view but not edit.
          {currentUser?.is_admin && (
            <button
              onClick={async () => {
                try {
                  await axios.post(
                    `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/lock-release`,
                    {}, { headers: authHeaders(token) },
                  );
                  await claimLock();
                } catch (e) { setError('Force-release failed'); }
              }}
              style={{ ...styles.btnDanger, marginLeft: '12px' }}
            >Force release</button>
          )}
        </div>
      )}
      {error && <div style={styles.banner('#ef4444')}>{error} <button onClick={() => setError('')} style={{ ...styles.btnSecondary, marginLeft: '8px', padding: '2px 6px' }}>Dismiss</button></div>}

      {/* Publish bar */}
      <div style={styles.publishBar}>
        <SaveStateBadge state={saveState} />
        <span style={{ flex: 1, color: 'var(--text-secondary)' }}>
          {hasUnpublished
            ? <strong style={{ color: '#f59e0b' }}>● Unpublished changes</strong>
            : (layout.published_at
              ? `Last published: ${new Date(layout.published_at).toLocaleString()}`
              : 'Never published — draft is invisible to workers until you Publish')}
        </span>
        {!readOnly && (
          <>
            <button
              onClick={undo}
              disabled={!canUndo}
              aria-label="Undo (Ctrl+Z)"
              title="Undo (Ctrl+Z / ⌘Z)"
              style={{ ...styles.btnSecondary, opacity: canUndo ? 1 : 0.4, cursor: canUndo ? 'pointer' : 'default' }}
            >↶ Undo</button>
            <button
              onClick={redo}
              disabled={!canRedo}
              aria-label="Redo (Ctrl+Shift+Z)"
              title="Redo (Ctrl+Shift+Z / ⌘⇧Z)"
              style={{ ...styles.btnSecondary, opacity: canRedo ? 1 : 0.4, cursor: canRedo ? 'pointer' : 'default' }}
            >↷ Redo</button>
          </>
        )}
        {tab === 'canvas' && (
          <div role="group" aria-label="Preview viewport" style={{ display: 'flex', gap: '2px', marginLeft: '4px' }}>
            {[
              ['edit', '✎', 'Edit'],
              ['mobile', '📱', 'Mobile preview (375px)'],
              ['tablet', '📲', 'Tablet preview (768px)'],
              ['desktop', '🖥', 'Desktop preview'],
            ].map(([mode, icon, label]) => (
              <button
                key={mode}
                onClick={() => setPreviewMode(mode)}
                aria-label={label}
                aria-pressed={previewMode === mode}
                title={label}
                style={{
                  ...styles.btnSecondary,
                  background: previewMode === mode ? 'var(--accent-color)' : 'var(--bg-tertiary)',
                  color: previewMode === mode ? '#fff' : 'var(--text-primary)',
                  padding: '6px 8px',
                  borderRadius: '4px',
                }}
              >{icon}</button>
            ))}
          </div>
        )}
        {hasUnpublished && layout.has_published && !readOnly && (
          <button onClick={doDiscardDraft} style={styles.btnSecondary}>Discard draft</button>
        )}
        <button onClick={() => setShowWiresDialog(true)} style={styles.btnSecondary} title="View every wire in this layout">
          Wires ({Array.isArray(events) ? events.length : 0})
        </button>
        {!readOnly && (
          <button
            onClick={doSave}
            disabled={saveState === 'saving' || saveState === 'saved'}
            title="Save draft (Ctrl+S / ⌘S). Auto-saves every 10s."
            style={{
              ...styles.btnSecondary,
              opacity: (saveState === 'saving' || saveState === 'saved') ? 0.6 : 1,
              cursor: (saveState === 'saving' || saveState === 'saved') ? 'default' : 'pointer',
            }}
          >Save</button>
        )}
        {!readOnly && (
          <button onClick={() => setShowPublishDialog(true)} style={styles.btnPrimary} disabled={saveState === 'saving'}>
            Publish
          </button>
        )}
      </div>

      {/* Body */}
      {tab === 'canvas' && previewMode === 'edit' && (
        <div style={styles.body}>
          <Palette
            category={layout.category}
            onAdd={(t) => {
              // Add to root if nothing selected, else to selected (if container) or its parent (otherwise).
              const target = selectedId ? findById(tree, selectedId) : null;
              const meta = target && getWidget(target.type);
              if (target && meta?.isContainer) addWidget(target.id, t);
              else addWidget(tree.id, t);
            }}
            onPaletteDragStart={(t) => { dragRef.current = { kind: 'palette', widgetType: t }; setIsDragging(true); }}
            onPaletteDragEnd={() => { dragRef.current = null; setIsDragging(false); }}
            readOnly={readOnly}
            rootIsLeaf={rootIsLeaf}
            onWrapRoot={wrapRootInStack}
          />
          <Canvas
            tree={tree}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onRemove={removeWidget}
            readOnly={readOnly}
            isDragging={isDragging}
            onNodeDragStart={(id) => { dragRef.current = { kind: 'move', id }; setIsDragging(true); }}
            onNodeDragEnd={() => { dragRef.current = null; setIsDragging(false); }}
            onDropAt={(parentId, idx) => {
              const d = dragRef.current;
              if (!d) return;
              if (d.kind === 'palette') addWidget(parentId, d.widgetType, idx);
              else if (d.kind === 'move') moveWidget(d.id, parentId, idx);
              dragRef.current = null;
              setIsDragging(false);
            }}
          />
          <Inspector
            selected={selected}
            tree={tree}
            events={events}
            setEvents={setEvents}
            onUpdateConfig={updateConfig}
            onRemove={removeWidget}
            readOnly={readOnly}
            category={layout.category}
            workspaceId={workspaceId}
          />
        </div>
      )}
      {tab === 'canvas' && previewMode !== 'edit' && (
        <PreviewPane
          mode={previewMode}
          tree={tree}
          events={events}
          category={layout.category}
          token={token}
          workspaceId={workspaceId}
          currentUser={currentUser}
        />
      )}

      {tab === 'bindings' && (
        <BindingsPanel
          token={token}
          workspaceId={workspaceId}
          layoutId={layoutId}
          layout={layout}
        />
      )}

      {tab === 'history' && (
        <HistoryTab
          token={token}
          workspaceId={workspaceId}
          layoutId={layoutId}
          onRestored={loadLayout}
          readOnly={readOnly}
        />
      )}

      {showPublishDialog && (
        <PublishDialog
          onCancel={() => setShowPublishDialog(false)}
          onConfirm={doPublish}
        />
      )}
      {showWiresDialog && (
        <WiresDialog
          tree={tree}
          events={events}
          setEvents={setEvents}
          readOnly={readOnly}
          onClose={() => setShowWiresDialog(false)}
        />
      )}
      {confirmDialog}
    </div>
  );
}


function SaveStateBadge({ state }) {
  const map = {
    idle:   { color: 'var(--text-secondary)', label: '' },
    dirty:  { color: '#f59e0b',                label: '● Unsaved' },
    saving: { color: '#3b82f6',                label: '○ Saving…' },
    saved:  { color: '#10b981',                label: '✓ Saved' },
    error:  { color: '#ef4444',                label: '✕ Save failed' },
  };
  const s = map[state] || map.idle;
  return <span style={{ color: s.color, fontWeight: 600, minWidth: '90px' }}>{s.label}</span>;
}


// ─── Palette ──────────────────────────────────────────────────────────────
// ─── Preview Pane (Phase L6) ──────────────────────────────────────────────
// Renders the live draft tree through LayoutRenderer in a viewport-constrained
// frame so admins can sanity-check Task / Add Case / etc. layouts at phone
// dimensions without leaving the designer. Mock case + work item context lets
// data widgets render shells; real binding-driven preview belongs in
// /preview (deferred — needs a designer-time case-type picker).
const PREVIEW_WIDTHS = { mobile: 375, tablet: 768, desktop: 1280 };
const PREVIEW_LABELS = { mobile: 'Mobile · 375 px', tablet: 'Tablet · 768 px', desktop: 'Desktop · 1280 px' };

function PreviewPane({ mode, tree, events, category, token, workspaceId, currentUser }) {
  const width = PREVIEW_WIDTHS[mode] || 1280;
  const label = PREVIEW_LABELS[mode] || mode;
  const mockCase = useMemo(() => ({
    id: 'preview-case-id',
    case_number: 'PREVIEW-0001',
    title: 'Preview case',
    status: 'open',
    priority: 'medium',
    custom_fields: {},
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }), []);
  const mockWorkItem = useMemo(() => ({
    id: 'preview-work-item-id',
    case_id: mockCase.id,
    step_name: 'preview_step',
    status: 'in_progress',
    response_data: {},
  }), [mockCase.id]);
  const mockDraft = useMemo(() => ({
    picked: null,
    form: { title: '', description: '', priority: 'medium', custom_fields: {} },
    intakeFormDef: null,
    intakeFormValues: {},
    busy: false,
    error: null,
    taskType: null,
    formDef: null,
    values: {},
  }), []);

  return (
    <div
      style={{ ...styles.paneCenter, padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', background: 'var(--bg-primary)' }}
      role="region"
      aria-label={`Layout preview at ${label}`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label} — preview only (mock data, no live binding)
        </div>
        <div style={{
          width: `${width}px`,
          maxWidth: '100%',
          minHeight: '480px',
          maxHeight: 'calc(100vh - 220px)',
          border: mode === 'mobile' ? '12px solid #111827' : '8px solid #1f2937',
          borderRadius: mode === 'mobile' ? '32px' : '12px',
          background: 'var(--bg-primary)',
          overflow: 'auto',
          boxShadow: '0 14px 40px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <LayoutRenderer
            category={category}
            token={token}
            workspaceId={workspaceId}
            currentUser={currentUser}
            role={null}
            case={mockCase}
            workItem={mockWorkItem}
            draft={mockDraft}
            setDraft={() => { /* no-op in preview */ }}
            treeOverride={tree}
            eventsOverride={events}
            callbacks={{
              renderBuiltin: () => (
                <div style={{ padding: '20px', color: 'var(--text-secondary)', fontSize: '12px', fontStyle: 'italic' }}>
                  [Default surface — preview uses mock data]
                </div>
              ),
              onSubmit: () => {},
              onCancel: () => {},
              onPickAgain: () => {},
              onCaseUpdated: () => {},
              onOpenCase: () => {},
              onOpenWorkItem: () => {},
              onDirtyChange: () => {},
            }}
          />
        </div>
      </div>
    </div>
  );
}


function Palette({ category, onAdd, onPaletteDragStart, onPaletteDragEnd, readOnly, rootIsLeaf, onWrapRoot }) {
  const widgets = useMemo(() => {
    const all = listWidgets({ category });
    // Group by widget.group; preserve insertion order within groups
    const groups = new Map();
    for (const w of all) {
      const g = w.group || 'Other';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g).push(w);
    }
    return Array.from(groups.entries());
  }, [category]);

  return (
    <div style={styles.pane} role="region" aria-label="Widget palette">
      <div style={styles.paneTitle}>Widgets</div>
      {rootIsLeaf && !readOnly && (
        <div style={{ margin: '8px', padding: '8px 10px', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid #f59e0b', borderRadius: '4px', fontSize: '11px', color: 'var(--text-primary)', lineHeight: 1.4 }}>
          The root is a leaf widget — children added here won't render. Wrap it in a Stack so you can add siblings.
          <button
            onClick={onWrapRoot}
            style={{ display: 'block', marginTop: '6px', background: '#f59e0b', color: '#000', border: 'none', borderRadius: '3px', padding: '4px 8px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
          >Wrap root in Stack</button>
        </div>
      )}
      {widgets.map(([group, items]) => (
        <div key={group} style={{ padding: '8px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', padding: '4px 12px' }}>{group}</div>
          {items.map(w => (
            <button
              key={w.type}
              onClick={() => onAdd(w.type)}
              draggable={!readOnly}
              onDragStart={(e) => {
                if (readOnly) { e.preventDefault(); return; }
                // 'copyMove' so drop targets can pick either dropEffect — 'copy'
                // for new palette widgets, 'move' for re-parenting canvas
                // nodes. effectAllowed='copy' alone causes the browser to
                // silently reject any target with dropEffect='move'.
                e.dataTransfer.effectAllowed = 'copyMove';
                // setData is required on Firefox even when we read state from the drag ref.
                try { e.dataTransfer.setData('text/plain', `xavvy:palette:${w.type}`); } catch (_) { /* noop */ }
                onPaletteDragStart && onPaletteDragStart(w.type);
              }}
              onDragEnd={() => { onPaletteDragEnd && onPaletteDragEnd(); }}
              disabled={readOnly}
              title={w.description}
              style={{
                display: 'block',
                width: 'calc(100% - 16px)',
                margin: '2px 8px',
                padding: '7px 10px',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                cursor: readOnly ? 'not-allowed' : 'grab',
                fontSize: '12px',
                textAlign: 'left',
                opacity: readOnly ? 0.5 : 1,
              }}
            >{w.palette_label}</button>
          ))}
        </div>
      ))}
    </div>
  );
}


// ─── Canvas ───────────────────────────────────────────────────────────────
// The canvas renders the tree using each container's actual visual layout
// (Stack vertically, Columns as grid, Tabs as tab strip, Split as flex,
// Accordion as expandable sections) — rather than a single nested-card view.
// Containers delegate to a per-type renderer; leaf widgets show a card with
// label + description.
//
// Drag/drop:
//   - Palette items drag with kind='palette'; canvas chrome drags with
//     kind='move' (kind tracked in dragRef on the parent designer).
//   - Drop zones are <DropSlot parentId, idx orientation> elements
//     interleaved between siblings inside each container. They only show up
//     while a drag is in progress (otherwise gaps would clutter the canvas).
//   - The handler dispatched on drop (`onDropAt(parentId, idx)`) is what
//     consumes the dragRef and either inserts a fresh widget (palette) or
//     re-parents an existing node (move).
function Canvas({ tree, selectedId, onSelect, onRemove, readOnly, isDragging, onNodeDragStart, onNodeDragEnd, onDropAt }) {
  return (
    <div
      style={styles.paneCenter}
      tabIndex={0}
      role="tree"
      aria-label="Layout widget tree"
      aria-activedescendant={selectedId ? `canvas-node-${selectedId}` : undefined}
    >
      {tree
        ? <CanvasNode
            node={tree}
            depth={0}
            selectedId={selectedId}
            onSelect={onSelect}
            onRemove={onRemove}
            readOnly={readOnly}
            isDragging={isDragging}
            onNodeDragStart={onNodeDragStart}
            onNodeDragEnd={onNodeDragEnd}
            onDropAt={onDropAt}
            isRoot
          />
        : <div style={{ color: 'var(--text-secondary)' }}>Empty layout</div>}
    </div>
  );
}

// Drop indicator between sibling slots. Two visual variants:
//   - 'horizontal' (between rows): full-width bar; ~14px idle, 40px on hover
//   - 'vertical'   (between cols): full-height bar; ~14px idle, 40px on hover
// Visible only while a drag is in progress so idle canvas stays clean. The
// idle state is intentionally chunky enough (~14px) that users can hit it
// without surgical precision. stopPropagation here is critical so the
// container-body fallback drop doesn't double-fire on the same gesture.
function DropSlot({ parentId, idx, orientation, onDropAt, isDragging, readOnly, label }) {
  const [over, setOver] = React.useState(false);
  if (readOnly || !isDragging) return null;
  const isVert = orientation === 'vertical';
  const base = {
    transition: 'all 100ms ease',
    background: over ? 'var(--accent-color)' : 'rgba(99,102,241,0.22)',
    border: over ? '2px dashed var(--accent-color)' : '1px dashed rgba(99,102,241,0.55)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: over ? '#fff' : 'var(--text-secondary)',
    fontSize: '10px',
    fontWeight: 600,
    flexShrink: 0,
    pointerEvents: 'auto',
  };
  const dim = isVert
    ? { width: over ? '40px' : '14px', minHeight: '32px', alignSelf: 'stretch', margin: '0 2px' }
    : { height: over ? '40px' : '14px', width: '100%', margin: '2px 0' };
  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; }}
      onDragLeave={(e) => { e.stopPropagation(); setOver(false); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setOver(false); onDropAt(parentId, idx); }}
      style={{ ...base, ...dim }}
      aria-label={label || 'Drop position'}
    >
      {over ? (label || 'Drop') : '·'}
    </div>
  );
}

// Container-body fallback drop target. Wraps every container renderer so that
// dropping anywhere inside the container (not just on a between-sibling
// DropSlot) appends to the end. DropSlot drops stopPropagation, so this
// fallback only fires on misses. Idle: invisible. Hovered: subtle outline.
function ContainerBodyDrop({ parentId, onDropAt, isDragging, readOnly, children, style }) {
  const [over, setOver] = React.useState(false);
  if (readOnly) return <div style={style}>{children}</div>;
  return (
    <div
      onDragEnter={isDragging ? (e) => { e.preventDefault(); setOver(true); } : undefined}
      onDragOver={isDragging ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
      onDragLeave={isDragging ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); } : undefined}
      onDrop={isDragging ? (e) => { e.preventDefault(); setOver(false); onDropAt(parentId, -1); } : undefined}
      style={{
        ...style,
        outline: over ? '2px dashed var(--accent-color)' : style?.outline,
        outlineOffset: over ? '-4px' : undefined,
        background: over ? 'rgba(99,102,241,0.06)' : style?.background,
      }}
    >
      {children}
    </div>
  );
}

// Wraps a child node with selection chrome (border + drag handle + delete).
// The chrome itself is what's draggable — moves the whole subtree. Click
// selects. Delete button triggers onRemove. We keep the handles minimal so
// container layouts render close to their published appearance.
function NodeChrome({ node, depth, isSelected, isContainer, onSelect, onRemove, readOnly, onNodeDragStart, onNodeDragEnd, isRoot, children }) {
  const meta = getWidget(node.type);
  const label = meta?.palette_label || node.type;
  const draggable = !readOnly && !isRoot;
  return (
    <div
      id={`canvas-node-${node.id}`}
      role="treeitem"
      tabIndex={isSelected ? 0 : -1}
      aria-selected={isSelected}
      aria-level={depth + 1}
      aria-label={`${label} — ${node.type}`}
      draggable={draggable}
      onDragStart={draggable ? (e) => {
        e.stopPropagation();
        // 'copyMove' (not just 'move') for symmetry with palette so a single
        // dropEffect value on the target works for either source.
        e.dataTransfer.effectAllowed = 'copyMove';
        try { e.dataTransfer.setData('text/plain', `xavvy:move:${node.id}`); } catch (_) { /* noop */ }
        onNodeDragStart && onNodeDragStart(node.id);
      } : undefined}
      onDragEnd={draggable ? () => { onNodeDragEnd && onNodeDragEnd(); } : undefined}
      onClick={(e) => { e.stopPropagation(); onSelect(node.id); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          onSelect(node.id);
        }
      }}
      style={{
        position: 'relative',
        outline: isSelected ? '2px solid var(--accent-color)' : 'none',
        outlineOffset: '1px',
        borderRadius: '6px',
        cursor: draggable ? 'grab' : 'pointer',
        // outline-only selection so the visual layout (column widths etc.)
        // stays stable when toggling selection.
      }}
    >
      {children}
      {!isRoot && !readOnly && isSelected && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(node.id); }}
          aria-label={`Delete ${label}`}
          title="Delete widget"
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: 'var(--accent-color)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '11px',
            cursor: 'pointer',
            zIndex: 2,
          }}
        >×</button>
      )}
      {!isRoot && isSelected && (
        <div style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          background: 'var(--accent-color)',
          color: '#fff',
          fontSize: '10px',
          padding: '1px 5px',
          borderRadius: '3px',
          fontWeight: 600,
          pointerEvents: 'none',
          zIndex: 2,
        }}>{label}</div>
      )}
    </div>
  );
}

// Card body for non-container leaf widgets — preserves the existing
// "title + description" appearance from before L7 so admins still see what
// the widget represents (we can't render the actual widget without runtime
// context).
function LeafCard({ node }) {
  const meta = getWidget(node.type);
  const label = meta?.palette_label || node.type;
  const desc = meta?.description || '';
  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      background: 'var(--bg-secondary)',
      padding: '10px 12px',
      minHeight: '54px',
    }}>
      <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-primary)' }}>{label}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '3px' }}>
        {desc}
        <span style={{ marginLeft: '6px', opacity: 0.6 }}>· {node.id}</span>
      </div>
    </div>
  );
}

// Container renderer dispatch table. Defined as a function (not a constant
// map) so each renderer can be a regular component-style function that
// receives all DnD/selection plumbing.
function CanvasNode(props) {
  const { node } = props;
  const meta = getWidget(node.type);
  const isContainer = !!meta?.isContainer;
  const isSelected = node.id === props.selectedId;

  if (!isContainer) {
    return (
      <NodeChrome {...props} isContainer={false} isSelected={isSelected}>
        <LeafCard node={node} />
      </NodeChrome>
    );
  }

  // Containers — pick visual renderer per type. Default to stack behaviour
  // for any future container type we haven't specialised yet.
  const Renderer =
    node.type === 'container.columns'   ? ContainerColumnsCanvas
  : node.type === 'container.split'     ? ContainerSplitCanvas
  : node.type === 'container.tabs'      ? ContainerTabsCanvas
  : node.type === 'container.accordion' ? ContainerAccordionCanvas
  : ContainerStackCanvas;

  return (
    <NodeChrome {...props} isContainer={true} isSelected={isSelected}>
      <Renderer {...props} />
    </NodeChrome>
  );
}

// Helper: render the children of a container interleaved with horizontal
// drop slots. Used by Stack and Accordion-body fallback.
function renderChildrenWithDropZones(props, orientation) {
  const { node, depth, selectedId, onSelect, onRemove, readOnly, isDragging, onNodeDragStart, onNodeDragEnd, onDropAt } = props;
  const kids = Array.isArray(node.children) ? node.children : [];
  const out = [];
  out.push(
    <DropSlot
      key={`drop-${node.id}-0`}
      parentId={node.id}
      idx={0}
      orientation={orientation}
      onDropAt={onDropAt}
      isDragging={isDragging}
      readOnly={readOnly}
    />,
  );
  kids.forEach((c, i) => {
    out.push(
      <CanvasNode
        key={c.id}
        node={c}
        depth={depth + 1}
        selectedId={selectedId}
        onSelect={onSelect}
        onRemove={onRemove}
        readOnly={readOnly}
        isDragging={isDragging}
        onNodeDragStart={onNodeDragStart}
        onNodeDragEnd={onNodeDragEnd}
        onDropAt={onDropAt}
      />,
    );
    out.push(
      <DropSlot
        key={`drop-${node.id}-${i + 1}`}
        parentId={node.id}
        idx={i + 1}
        orientation={orientation}
        onDropAt={onDropAt}
        isDragging={isDragging}
        readOnly={readOnly}
      />,
    );
  });
  if (kids.length === 0) {
    out.push(
      <EmptyContainerHint
        key={`hint-${node.id}`}
        parentId={node.id}
        onDropAt={onDropAt}
        isDragging={isDragging}
        readOnly={readOnly}
      />,
    );
  }
  return out;
}

// Visible "drop here" placeholder for empty containers. Always visible (not
// gated on isDragging) so users can see where the container is.
function EmptyContainerHint({ parentId, onDropAt, isDragging, readOnly }) {
  const [over, setOver] = React.useState(false);
  return (
    <div
      onDragEnter={readOnly ? undefined : (e) => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
      onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; }}
      onDragLeave={readOnly ? undefined : (e) => { e.stopPropagation(); setOver(false); }}
      onDrop={readOnly ? undefined : (e) => { e.preventDefault(); e.stopPropagation(); setOver(false); onDropAt(parentId, 0); }}
      style={{
        border: over ? '2px dashed var(--accent-color)' : '2px dashed var(--border-color)',
        borderRadius: '6px',
        padding: '24px',
        textAlign: 'center',
        background: over ? 'rgba(99,102,241,0.12)' : 'transparent',
        color: 'var(--text-secondary)',
        fontSize: '11px',
        fontStyle: 'italic',
        margin: '4px',
        flex: 1,
        minHeight: '60px',
      }}
    >
      {isDragging ? 'Drop here to add to this container' : 'Empty container — drag a widget here, or select & click a palette item.'}
    </div>
  );
}

function ContainerStackCanvas(props) {
  const { node, isDragging, readOnly, onDropAt } = props;
  const padding = node.config?.padding === false ? 0 : 10;
  const gap = typeof node.config?.gap === 'number' ? node.config.gap : 8;
  return (
    <ContainerBodyDrop
      parentId={node.id}
      onDropAt={onDropAt}
      isDragging={isDragging}
      readOnly={readOnly}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: `${gap}px`,
        padding: `${padding}px`,
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        background: 'var(--bg-tertiary)',
        minHeight: '64px',
      }}
    >
      <ContainerLabel label="Stack" config={node.config} />
      {renderChildrenWithDropZones(props, 'horizontal')}
    </ContainerBodyDrop>
  );
}

function ContainerColumnsCanvas(props) {
  const { node, depth, selectedId, onSelect, onRemove, readOnly, isDragging, onNodeDragStart, onNodeDragEnd, onDropAt } = props;
  const cols = Math.max(1, Math.min(6, node.config?.columns || 2));
  const kids = Array.isArray(node.children) ? node.children : [];
  return (
    <ContainerBodyDrop
      parentId={node.id}
      onDropAt={onDropAt}
      isDragging={isDragging}
      readOnly={readOnly}
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        background: 'var(--bg-tertiary)',
        padding: '10px',
        minHeight: '64px',
      }}
    >
      <ContainerLabel label={`Columns × ${cols}`} config={node.config} />
      {kids.length === 0 ? (
        <EmptyContainerHint parentId={node.id} onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '8px' }}>
          {kids.map((c, i) => (
            <div key={c.id} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <DropSlot parentId={node.id} idx={i} orientation="horizontal" onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} label={`Col ${(i % cols) + 1} ↑`} />
              <CanvasNode
                node={c}
                depth={depth + 1}
                selectedId={selectedId}
                onSelect={onSelect}
                onRemove={onRemove}
                readOnly={readOnly}
                isDragging={isDragging}
                onNodeDragStart={onNodeDragStart}
                onNodeDragEnd={onNodeDragEnd}
                onDropAt={onDropAt}
              />
              {i === kids.length - 1 && (
                <DropSlot parentId={node.id} idx={i + 1} orientation="horizontal" onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} label="↓ end" />
              )}
            </div>
          ))}
        </div>
      )}
    </ContainerBodyDrop>
  );
}

function ContainerSplitCanvas(props) {
  const { node, isDragging, readOnly, onDropAt } = props;
  const dir = node.config?.direction === 'vertical' ? 'column' : 'row';
  const isHoriz = dir === 'row';
  return (
    <ContainerBodyDrop
      parentId={node.id}
      onDropAt={onDropAt}
      isDragging={isDragging}
      readOnly={readOnly}
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        background: 'var(--bg-tertiary)',
        padding: '10px',
        minHeight: '64px',
      }}
    >
      <ContainerLabel label={`Split — ${isHoriz ? 'horizontal' : 'vertical'}`} config={node.config} />
      <div style={{ display: 'flex', flexDirection: dir, gap: '8px', alignItems: 'stretch' }}>
        {renderChildrenWithDropZones(props, isHoriz ? 'vertical' : 'horizontal')}
      </div>
    </ContainerBodyDrop>
  );
}

function ContainerAccordionCanvas(props) {
  const { node, depth, selectedId, onSelect, onRemove, readOnly, isDragging, onNodeDragStart, onNodeDragEnd, onDropAt } = props;
  const kids = Array.isArray(node.children) ? node.children : [];
  const [openIdx, setOpenIdx] = React.useState(node.config?.default_open_index ?? 0);
  return (
    <ContainerBodyDrop
      parentId={node.id}
      onDropAt={onDropAt}
      isDragging={isDragging}
      readOnly={readOnly}
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        background: 'var(--bg-tertiary)',
        padding: '10px',
        minHeight: '64px',
      }}
    >
      <ContainerLabel label="Accordion" config={node.config} />
      {kids.length === 0 ? (
        <EmptyContainerHint parentId={node.id} onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <DropSlot parentId={node.id} idx={0} orientation="horizontal" onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} />
          {kids.map((c, i) => {
            const meta = getWidget(c.type);
            const title = c.config?.title || meta?.palette_label || `Section ${i + 1}`;
            const open = openIdx === i;
            return (
              <React.Fragment key={c.id}>
                <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setOpenIdx(open ? -1 : i); }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      color: 'var(--text-primary)',
                      border: 'none',
                      padding: '8px 12px',
                      fontWeight: 600,
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>{title}</span>
                    <span>{open ? '▾' : '▸'}</span>
                  </button>
                  {open && (
                    <div style={{ padding: '8px', borderTop: '1px solid var(--border-color)' }}>
                      <CanvasNode
                        node={c}
                        depth={depth + 1}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        onRemove={onRemove}
                        readOnly={readOnly}
                        isDragging={isDragging}
                        onNodeDragStart={onNodeDragStart}
                        onNodeDragEnd={onNodeDragEnd}
                        onDropAt={onDropAt}
                      />
                    </div>
                  )}
                </div>
                <DropSlot parentId={node.id} idx={i + 1} orientation="horizontal" onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} />
              </React.Fragment>
            );
          })}
        </div>
      )}
    </ContainerBodyDrop>
  );
}

function ContainerTabsCanvas(props) {
  const { node, depth, selectedId, onSelect, onRemove, readOnly, isDragging, onNodeDragStart, onNodeDragEnd, onDropAt } = props;
  const kids = Array.isArray(node.children) ? node.children : [];
  // Selecting a tab in the designer also selects the corresponding child
  // node, which mirrors what the runtime tabs container does and lets the
  // user inspect that tab's config in the right pane.
  const initial = kids.findIndex(c => c.id === selectedId);
  const [activeIdx, setActiveIdx] = React.useState(initial >= 0 ? initial : 0);
  React.useEffect(() => {
    if (initial >= 0 && initial !== activeIdx) setActiveIdx(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);
  const safeActive = Math.min(Math.max(0, activeIdx), Math.max(0, kids.length - 1));
  const activeChild = kids[safeActive] || null;
  return (
    <ContainerBodyDrop
      parentId={node.id}
      onDropAt={onDropAt}
      isDragging={isDragging}
      readOnly={readOnly}
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        background: 'var(--bg-tertiary)',
        padding: '10px',
        minHeight: '64px',
      }}
    >
      <ContainerLabel label="Tabs" config={node.config} />
      {kids.length === 0 ? (
        <EmptyContainerHint parentId={node.id} onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap', minHeight: '34px' }}>
            <DropSlot parentId={node.id} idx={0} orientation="vertical" onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} />
            {kids.map((c, i) => {
              const meta = getWidget(c.type);
              const label = c.config?.title || c.config?.tab_label || meta?.palette_label || `Tab ${i + 1}`;
              const isActive = i === safeActive;
              return (
                <React.Fragment key={c.id}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setActiveIdx(i); onSelect(c.id); }}
                    style={{
                      background: 'transparent',
                      color: isActive ? 'var(--accent-color)' : 'var(--text-secondary)',
                      border: 'none',
                      borderBottom: isActive ? '2px solid var(--accent-color)' : '2px solid transparent',
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >{label}</button>
                  <DropSlot parentId={node.id} idx={i + 1} orientation="vertical" onDropAt={onDropAt} isDragging={isDragging} readOnly={readOnly} />
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ padding: '10px 0 0' }}>
            {activeChild && (
              <CanvasNode
                node={activeChild}
                depth={depth + 1}
                selectedId={selectedId}
                onSelect={onSelect}
                onRemove={onRemove}
                readOnly={readOnly}
                isDragging={isDragging}
                onNodeDragStart={onNodeDragStart}
                onNodeDragEnd={onNodeDragEnd}
                onDropAt={onDropAt}
              />
            )}
          </div>
        </>
      )}
    </ContainerBodyDrop>
  );
}

function ContainerLabel({ label }) {
  return (
    <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', fontWeight: 600 }}>
      {label}
    </div>
  );
}


// ─── Inspector ────────────────────────────────────────────────────────────
function flattenTree(tree) {
  const out = [];
  const walk = (n) => {
    if (!n || typeof n !== 'object') return;
    out.push(n);
    if (Array.isArray(n.children)) n.children.forEach(walk);
  };
  walk(tree);
  return out;
}

function Inspector({ selected, tree, events, setEvents, onUpdateConfig, onRemove, readOnly, category, workspaceId }) {
  if (!selected) {
    return (
      <div style={styles.paneRight} role="region" aria-label="Widget inspector">
        <div style={styles.paneTitle}>Inspector</div>
        <div style={{ padding: '20px', color: 'var(--text-secondary)', fontSize: '12px' }}>
          Select a widget on the canvas to edit its settings.
        </div>
      </div>
    );
  }
  const meta = getWidget(selected.type);
  const fields = meta?.configSchema?.fields || [];
  return (
    <div style={styles.paneRight} role="region" aria-label="Widget inspector">
      <div style={styles.paneTitle}>Inspector — {meta?.palette_label || selected.type}</div>
      <div style={{ padding: '12px' }}>
        <Field label="Widget id">
          <input value={selected.id} readOnly style={{ ...styles.input, opacity: 0.6 }} />
        </Field>
        <Field label="Type">
          <input value={selected.type} readOnly style={{ ...styles.input, opacity: 0.6 }} />
        </Field>
        <Field label="Title">
          <input
            type="text"
            value={selected.config?.title ?? ''}
            onChange={(e) => onUpdateConfig(selected.id, { title: e.target.value })}
            disabled={readOnly}
            placeholder={meta?.palette_label || ''}
            style={styles.input}
          />
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '3px' }}>
            Tab/section label when this widget is a child of a Tabs or Accordion container. Falls back to the widget's palette name if blank.
          </div>
        </Field>
        {fields.length === 0 && (
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', padding: '8px 0' }}>
            This widget has no other configurable settings.
          </div>
        )}
        {fields.map(f => (
          <Field key={f.name} label={f.label || f.name}>
            <ConfigInput
              field={f}
              value={selected.config?.[f.name] ?? f.default ?? ''}
              onChange={(v) => onUpdateConfig(selected.id, { [f.name]: v })}
              disabled={readOnly}
              category={category}
              workspaceId={workspaceId}
            />
          </Field>
        ))}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          {!readOnly && selected.id !== 'root' && (
            <button onClick={() => onRemove(selected.id)} style={styles.btnDanger}>Delete widget</button>
          )}
        </div>
        <EventsPanel
          selected={selected}
          tree={tree}
          events={events}
          setEvents={setEvents}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}


// ─── EventsPanel ──────────────────────────────────────────────────────────
// Inspector sub-panel for Phase L4a wire CRUD. Lists wires touching the
// selected widget (as source or target), supports inline add/remove/toggle.
// The "All wires" view across the whole layout is intentionally deferred —
// most admins reason about wires per-widget and a tree-wide list is mostly
// noise.
function EventsPanel({ selected, tree, events, setEvents, readOnly }) {
  const meta = getWidget(selected.type);
  const allNodes = useMemo(
    () => flattenTree(tree).filter(n => n && n.id && n.id !== selected.id),
    [tree, selected.id],
  );
  const wireList = Array.isArray(events) ? events : [];

  const wiresAsSource = wireList.filter(w => w.from?.widget_id === selected.id);
  const wiresAsTarget = wireList.filter(w => w.to?.widget_id === selected.id);

  const addWire = (wire) => setEvents(prev => [...(Array.isArray(prev) ? prev : []), wire]);
  const removeWire = (id) => setEvents(prev => (Array.isArray(prev) ? prev : []).filter(w => w.id !== id));
  const toggleWire = (id) => setEvents(prev => (Array.isArray(prev) ? prev : []).map(w => (
    w.id === id ? { ...w, enabled: w.enabled === false ? true : false } : w
  )));
  const updateWire = (id, patch) => setEvents(prev => (Array.isArray(prev) ? prev : []).map(w => (
    w.id === id ? { ...w, ...patch } : w
  )));

  return (
    <div style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>EVENTS</div>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
        <div>Publishes: {meta?.publishes?.length ? meta.publishes.join(', ') : '—'}</div>
        <div>Subscribes: {meta?.subscribes?.length ? meta.subscribes.join(', ') : '—'}</div>
        <div>Actions: {meta?.actions?.length ? meta.actions.map(a => a.name).join(', ') : '—'}</div>
      </div>

      <div style={{ fontSize: '11px', fontWeight: 600, marginTop: '10px', marginBottom: '4px' }}>
        Outbound wires <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({wiresAsSource.length})</span>
      </div>
      {wiresAsSource.length === 0 && (
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '6px' }}>
          No wires from this widget.
        </div>
      )}
      {wiresAsSource.map(w => (
        <WireRow
          key={w.id}
          wire={w}
          tree={tree}
          direction="from"
          onRemove={() => removeWire(w.id)}
          onToggle={() => toggleWire(w.id)}
          onPatch={(patch) => updateWire(w.id, patch)}
          readOnly={readOnly}
        />
      ))}

      <div style={{ fontSize: '11px', fontWeight: 600, marginTop: '12px', marginBottom: '4px' }}>
        Inbound wires <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({wiresAsTarget.length})</span>
      </div>
      {wiresAsTarget.length === 0 && (
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '6px' }}>
          No wires firing actions on this widget.
        </div>
      )}
      {wiresAsTarget.map(w => (
        <WireRow
          key={w.id}
          wire={w}
          tree={tree}
          direction="to"
          onRemove={() => removeWire(w.id)}
          onToggle={() => toggleWire(w.id)}
          onPatch={(patch) => updateWire(w.id, patch)}
          readOnly={readOnly}
        />
      ))}

      {!readOnly && (
        <AddWireForm
          sourceWidget={selected}
          sourceMeta={meta}
          allNodes={allNodes}
          existingIds={new Set(wireList.map(w => w.id))}
          onAdd={addWire}
        />
      )}
    </div>
  );
}

function WireRow({ wire, tree, direction, onRemove, onToggle, onPatch, readOnly }) {
  const otherEnd = direction === 'from' ? wire.to : wire.from;
  const otherNode = useMemo(() => {
    if (!otherEnd?.widget_id) return null;
    return flattenTree(tree).find(n => n.id === otherEnd.widget_id) || null;
  }, [tree, otherEnd?.widget_id]);
  const otherMeta = otherNode ? getWidget(otherNode.type) : null;
  const otherLabel = otherNode?.config?.title || otherMeta?.palette_label || otherEnd?.widget_id || '(missing widget)';
  const enabled = wire.enabled !== false;
  const dim = !enabled ? { opacity: 0.5 } : {};
  const hasTransform = typeof wire.transform_js === 'string' && wire.transform_js.trim().length > 0;
  const [transformOpen, setTransformOpen] = useState(hasTransform);
  return (
    <div style={{ ...dim, border: '1px solid var(--border-color)', borderRadius: '6px', padding: '6px 8px', marginBottom: '6px', background: 'var(--bg-tertiary)' }}>
      <div style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
        <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {direction === 'from' ? (
            <>
              <code style={{ fontSize: '10px' }}>{wire.from.topic}</code>
              <span style={{ color: 'var(--text-secondary)' }}> → </span>
              <strong>{otherLabel}</strong>
              <span style={{ color: 'var(--text-secondary)' }}>.{wire.to?.action}</span>
            </>
          ) : (
            <>
              <strong>{otherLabel}</strong>
              <span style={{ color: 'var(--text-secondary)' }}> publishes </span>
              <code style={{ fontSize: '10px' }}>{wire.from.topic}</code>
              <span style={{ color: 'var(--text-secondary)' }}> → </span>
              <span>.{wire.to?.action}</span>
            </>
          )}
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            <button onClick={() => setTransformOpen(o => !o)} title="Edit payload mapping" style={{ ...styles.btnSecondary, padding: '2px 6px', fontSize: '10px', borderColor: hasTransform ? 'var(--accent-color)' : undefined, color: hasTransform ? 'var(--accent-color)' : undefined }}>
              {hasTransform ? 'fx' : '+fx'}
            </button>
            <button onClick={onToggle} title={enabled ? 'Disable' : 'Enable'} style={{ ...styles.btnSecondary, padding: '2px 6px', fontSize: '10px' }}>
              {enabled ? 'On' : 'Off'}
            </button>
            <button onClick={onRemove} title="Remove wire" style={{ ...styles.btnDanger, padding: '2px 6px', fontSize: '10px' }}>×</button>
          </div>
        )}
      </div>
      <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>id: {wire.id}</div>
      {!readOnly && transformOpen && onPatch && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--border-color)' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: 4 }}>
            Payload mapping (optional) — runs in the iframe sandbox before dispatch. <code>payload</code>, <code>ctx</code>, <code>helpers</code> in scope; return the new payload.
          </div>
          <ScriptEditor
            value={wire.transform_js || ''}
            onChange={(v) => onPatch({ transform_js: v })}
            placeholder={'// e.g. return { id: payload.case_id, label: helpers.truncate(payload.title, 40) };'}
            minHeight={80}
          />
          {hasTransform && (
            <button
              onClick={() => onPatch({ transform_js: '' })}
              style={{ ...styles.btnSecondary, padding: '2px 6px', fontSize: '10px', marginTop: 4 }}
            >Clear mapping</button>
          )}
        </div>
      )}
    </div>
  );
}

function AddWireForm({ sourceWidget, sourceMeta, allNodes, existingIds, onAdd }) {
  const sourceTopics = sourceMeta?.publishes?.length ? sourceMeta.publishes : [];
  const [topic, setTopic] = useState(sourceTopics[0] || '');
  const [targetId, setTargetId] = useState('');
  const [action, setAction] = useState('');

  const targetMeta = targetId ? getWidget((allNodes.find(n => n.id === targetId) || {}).type) : null;
  const targetActions = targetMeta?.actions || [];

  // Reset action when target changes if current action isn't valid for the new target.
  useEffect(() => {
    if (!targetActions.some(a => a.name === action)) {
      setAction(targetActions[0]?.name || '');
    }
  }, [targetId, targetActions, action]);

  const canSubmit = topic && targetId && action;

  const submit = () => {
    if (!canSubmit) return;
    let id = `wire_${Math.random().toString(36).slice(2, 10)}`;
    while (existingIds.has(id)) id = `wire_${Math.random().toString(36).slice(2, 10)}`;
    onAdd({
      id,
      from: { widget_id: sourceWidget.id, topic },
      to:   { widget_id: targetId, action },
      enabled: true,
    });
    // Keep topic + target so admins can stack actions; reset action so each
    // submit is an explicit pick.
    setAction(targetActions[0]?.name || '');
  };

  if (sourceTopics.length === 0) {
    return (
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '12px' }}>
        This widget declares no publish topics — nothing to wire from. Add a wire from a different widget that publishes to a topic this one's actions should react to.
      </div>
    );
  }

  return (
    <div style={{ marginTop: '12px', padding: '8px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Add wire</div>
      <Field label="When this widget publishes">
        <select value={topic} onChange={(e) => setTopic(e.target.value)} style={styles.input}>
          {sourceTopics.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Target widget">
        <select value={targetId} onChange={(e) => setTargetId(e.target.value)} style={styles.input}>
          <option value="">— pick a widget —</option>
          {allNodes.filter(n => (getWidget(n.type)?.actions || []).length > 0).map(n => {
            const m = getWidget(n.type);
            const lbl = n.config?.title || m?.palette_label || n.type;
            return <option key={n.id} value={n.id}>{lbl} ({n.id})</option>;
          })}
        </select>
        {targetId === '' && (
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '3px' }}>
            Only widgets with declared actions appear here. Add an action to a widget's registration to make it wireable as a target.
          </div>
        )}
      </Field>
      <Field label="Run this action">
        <select value={action} onChange={(e) => setAction(e.target.value)} disabled={!targetId} style={styles.input}>
          {targetActions.length === 0 && <option value="">(no actions)</option>}
          {targetActions.map(a => <option key={a.name} value={a.name}>{a.label || a.name}</option>)}
        </select>
      </Field>
      <button onClick={submit} disabled={!canSubmit} style={{ ...styles.btnPrimary, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>Add wire</button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
      {children}
    </div>
  );
}

function ConfigInput({ field, value, onChange, disabled, category, workspaceId }) {
  const t = field.type || 'text';
  if (t === 'boolean') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        /> {field.description || ''}
      </label>
    );
  }
  if (t === 'number') {
    return (
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        disabled={disabled}
        min={field.min}
        max={field.max}
        style={styles.input}
      />
    );
  }
  if (t === 'select') {
    return (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={styles.input}
      >
        {(field.options || []).map(o => (
          <option key={o.value} value={o.value}>{o.label || o.value}</option>
        ))}
      </select>
    );
  }
  if (t === 'textarea') {
    // Phase L4b — script bodies pipe through the dedicated ScriptEditor for
    // syntax highlighting + the "Available in scope" hint. Other textarea
    // fields (e.g. addCaseGuidance.markdown) use a plain textarea.
    if (field.editor === 'script') {
      return (
        <ScriptEditorWithPreview
          value={value ?? ''}
          onChange={(v) => onChange(v)}
          placeholder={field.placeholder || ''}
          minHeight={field.min_height || 160}
          kind="script"
          category={category}
          workspaceId={workspaceId}
        />
      );
    }
    return (
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={field.rows || 4}
        style={{ ...styles.input, height: 'auto', resize: 'vertical', fontFamily: field.monospace ? "'Fira Code', monospace" : undefined }}
      />
    );
  }
  // text default
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={styles.input}
    />
  );
}


// ─── WiresDialog ──────────────────────────────────────────────────────────
// Workspace-level view of every wire in the layout, in one place. The Inspector
// EventsPanel is per-widget and good for "what wires touch this widget?"; this
// modal answers "what is happening across the whole layout?" with row-level
// add/remove/toggle and the same payload-mapping editor as the Inspector.
function WiresDialog({ tree, events, setEvents, readOnly, onClose }) {
  const allNodes = useMemo(() => flattenTree(tree).filter(n => n && n.id), [tree]);
  const wireList = Array.isArray(events) ? events : [];

  const removeWire = (id) => setEvents(prev => (Array.isArray(prev) ? prev : []).filter(w => w.id !== id));
  const toggleWire = (id) => setEvents(prev => (Array.isArray(prev) ? prev : []).map(w => (
    w.id === id ? { ...w, enabled: w.enabled === false ? true : false } : w
  )));
  const updateWire = (id, patch) => setEvents(prev => (Array.isArray(prev) ? prev : []).map(w => (
    w.id === id ? { ...w, ...patch } : w
  )));

  const labelFor = (widgetId) => {
    const node = allNodes.find(n => n.id === widgetId);
    if (!node) return widgetId || '(missing)';
    const meta = getWidget(node.type);
    return node.config?.title || meta?.palette_label || node.type;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, width: 'min(900px, 95vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border-color)' }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>All wires <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({wireList.length})</span></h3>
          <button onClick={onClose} style={styles.btnSecondary}>Close</button>
        </div>
        <div style={{ padding: 12, overflowY: 'auto', flex: 1 }}>
          {wireList.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', padding: 12 }}>
              No wires in this layout. Open a widget's Inspector → Events panel to add the first one, or wire from a Script Widget for behaviour without dedicated topics.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={wireDialogTh}>From</th>
                  <th style={wireDialogTh}>Topic</th>
                  <th style={wireDialogTh}>To</th>
                  <th style={wireDialogTh}>Action</th>
                  <th style={wireDialogTh}>Mapping</th>
                  <th style={wireDialogTh}></th>
                </tr>
              </thead>
              <tbody>
                {wireList.map(w => {
                  const enabled = w.enabled !== false;
                  const hasTransform = typeof w.transform_js === 'string' && w.transform_js.trim().length > 0;
                  return (
                    <WireDialogRow
                      key={w.id}
                      wire={w}
                      enabled={enabled}
                      hasTransform={hasTransform}
                      labelFor={labelFor}
                      readOnly={readOnly}
                      onToggle={() => toggleWire(w.id)}
                      onRemove={() => removeWire(w.id)}
                      onPatch={(patch) => updateWire(w.id, patch)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const wireDialogTh = { textAlign: 'left', padding: '6px 8px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' };
const wireDialogTd = { padding: '6px 8px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top' };

function WireDialogRow({ wire, enabled, hasTransform, labelFor, readOnly, onToggle, onRemove, onPatch }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr style={{ opacity: enabled ? 1 : 0.5 }}>
        <td style={wireDialogTd}><strong>{labelFor(wire.from?.widget_id)}</strong><div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{wire.from?.widget_id}</div></td>
        <td style={wireDialogTd}><code style={{ fontSize: 11 }}>{wire.from?.topic}</code></td>
        <td style={wireDialogTd}><strong>{labelFor(wire.to?.widget_id)}</strong><div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{wire.to?.widget_id}</div></td>
        <td style={wireDialogTd}><code style={{ fontSize: 11 }}>{wire.to?.action}</code></td>
        <td style={wireDialogTd}>
          {!readOnly ? (
            <button onClick={() => setOpen(o => !o)} style={{ ...styles.btnSecondary, padding: '2px 6px', fontSize: 10, borderColor: hasTransform ? 'var(--accent-color)' : undefined, color: hasTransform ? 'var(--accent-color)' : undefined }}>
              {hasTransform ? (open ? 'fx ▾' : 'fx ▸') : (open ? '+fx ▾' : '+fx ▸')}
            </button>
          ) : (hasTransform ? <span style={{ fontSize: 11, color: 'var(--accent-color)' }}>fx</span> : <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>—</span>)}
        </td>
        <td style={wireDialogTd}>
          {!readOnly && (
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={onToggle} style={{ ...styles.btnSecondary, padding: '2px 6px', fontSize: 10 }}>{enabled ? 'On' : 'Off'}</button>
              <button onClick={onRemove} style={{ ...styles.btnDanger, padding: '2px 6px', fontSize: 10 }}>×</button>
            </div>
          )}
        </td>
      </tr>
      {open && !readOnly && (
        <tr>
          <td colSpan={6} style={{ ...wireDialogTd, background: 'var(--bg-tertiary)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Payload mapping (optional). Runs in the iframe sandbox before dispatch. <code>payload</code>, <code>ctx</code>, <code>helpers</code> in scope; return the new payload.
            </div>
            <ScriptEditor
              value={wire.transform_js || ''}
              onChange={(v) => onPatch({ transform_js: v })}
              placeholder={'// e.g. return { ...payload, label: helpers.truncate(payload.title, 40) };'}
              minHeight={100}
            />
            {hasTransform && (
              <button
                onClick={() => onPatch({ transform_js: '' })}
                style={{ ...styles.btnSecondary, padding: '2px 6px', fontSize: 10, marginTop: 4 }}
              >Clear mapping</button>
            )}
          </td>
        </tr>
      )}
    </>
  );
}


// ─── PublishDialog ────────────────────────────────────────────────────────
function PublishDialog({ onCancel, onConfirm }) {
  const [note, setNote] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--bg-secondary)', padding: '20px', borderRadius: '8px', width: '420px', maxWidth: '95vw', border: '1px solid var(--border-color)' }}>
        <h3 style={{ margin: 0, fontSize: '15px' }}>Publish layout</h3>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0' }}>
          Promotes the current draft to live and snapshots a version. Workers see the new layout immediately.
        </p>
        <Field label="Change note (optional)">
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            placeholder="What's changing?"
            style={{ ...styles.input, height: '60px', resize: 'vertical' }}
          />
        </Field>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button onClick={onCancel} style={styles.btnSecondary}>Cancel</button>
          <button onClick={() => onConfirm(note)} style={styles.btnPrimary}>Publish</button>
        </div>
      </div>
    </div>
  );
}


// ─── Bindings panel ───────────────────────────────────────────────────────
function BindingsPanel({ token, workspaceId, layoutId, layout }) {
  const [bindings, setBindings] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ case_type_id: '', role_id: '', step_name: '', task_type_id: '' });
  const [error, setError] = useState('');
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, ct, r] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layout-bindings?category=${layout.category}`, { headers: authHeaders(token) }),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types`, { headers: authHeaders(token) }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles`, { headers: authHeaders(token) }).catch(() => ({ data: [] })),
      ]);
      setBindings((b.data || []).filter(x => x.layout_id === layoutId));
      setCaseTypes(ct.data || []);
      setRoles(r.data || []);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to load bindings'); }
    finally { setLoading(false); }
  }, [token, workspaceId, layoutId, layout.category]);

  useEffect(() => { load(); }, [load]);

  const addBinding = useCallback(async () => {
    setError('');
    try {
      const body = {
        category: layout.category,
        case_type_id: draft.case_type_id || null,
        role_id: draft.role_id || null,
        step_name: layout.category === 'task' ? (draft.step_name || null) : null,
        task_type_id: layout.category === 'add_task' ? (draft.task_type_id || null) : null,
        layout_id: layoutId,
      };
      await axios.put(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layout-bindings`, body, { headers: authHeaders(token) });
      setDraft({ case_type_id: '', role_id: '', step_name: '', task_type_id: '' });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Bind failed');
    }
  }, [draft, layout.category, layoutId, token, workspaceId, load]);

  const removeBinding = useCallback(async (id) => {
    const ok = await confirm({
      title: 'Remove binding?',
      message: 'Workers matching this binding will fall back to the next-most-specific binding (or the built-in default).',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layout-bindings/${id}`, { headers: authHeaders(token) });
      await load();
    } catch (e) { setError(e?.response?.data?.detail || 'Remove failed'); }
  }, [token, workspaceId, load, confirm]);

  const requireCaseType = layout.category !== 'home';

  return (
    <div style={{ padding: '20px', overflow: 'auto' }}>
      <h3 style={{ marginTop: 0 }}>Bindings</h3>
      <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
        Bindings tell the resolver which (case type, role{layout.category === 'task' ? ', step' : (layout.category === 'add_task' ? ', task type' : '')}) tuples should render this layout. Leave a key blank to wildcard it.
      </p>
      {error && <div style={styles.banner('#ef4444')}>{error}</div>}

      <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px', marginBottom: '14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
          {requireCaseType && (
            <Field label="Case type">
              <select value={draft.case_type_id} onChange={e => setDraft({ ...draft, case_type_id: e.target.value })} style={styles.input}>
                <option value="">— Any —</option>
                {caseTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Role">
            <select value={draft.role_id} onChange={e => setDraft({ ...draft, role_id: e.target.value })} style={styles.input}>
              <option value="">— Any —</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          {layout.category === 'task' && (
            <Field label="Step name">
              <input value={draft.step_name} onChange={e => setDraft({ ...draft, step_name: e.target.value })} style={styles.input} placeholder="(optional)" />
            </Field>
          )}
          {layout.category === 'add_task' && (
            <Field label="Task type id">
              <input value={draft.task_type_id} onChange={e => setDraft({ ...draft, task_type_id: e.target.value })} style={styles.input} placeholder="(optional)" />
            </Field>
          )}
        </div>
        <button onClick={addBinding} style={{ ...styles.btnPrimary, marginTop: '10px' }}>+ Add binding</button>
      </div>

      {loading && <div>Loading…</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ background: 'var(--bg-secondary)' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>Case type</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>Role</th>
            {layout.category === 'task' && <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>Step</th>}
            {layout.category === 'add_task' && <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>Task type</th>}
            <th style={{ width: '60px' }}></th>
          </tr>
        </thead>
        <tbody>
          {bindings.map(b => (
            <tr key={b.id}>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>
                {b.case_type_id ? (caseTypes.find(c => c.id === b.case_type_id)?.name || b.case_type_id) : <em>any</em>}
              </td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>
                {b.role_id ? (roles.find(r => r.id === b.role_id)?.name || b.role_id) : <em>any</em>}
              </td>
              {layout.category === 'task' && <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>{b.step_name || <em>any</em>}</td>}
              {layout.category === 'add_task' && <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>{b.task_type_id || <em>any</em>}</td>}
              <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>
                <button onClick={() => removeBinding(b.id)} style={{ ...styles.btnDanger, padding: '2px 6px' }}>×</button>
              </td>
            </tr>
          ))}
          {!loading && bindings.length === 0 && (
            <tr><td colSpan={5} style={{ padding: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>No bindings yet — this layout is invisible to the resolver.</td></tr>
          )}
        </tbody>
      </table>
      {confirmDialog}
    </div>
  );
}


// ─── HistoryTab ───────────────────────────────────────────────────────────
function HistoryTab({ token, workspaceId, layoutId, onRestored, readOnly }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/versions`, { headers: authHeaders(token) });
      setVersions(r.data || []);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to load history'); }
    finally { setLoading(false); }
  }, [token, workspaceId, layoutId]);

  useEffect(() => { load(); }, [load]);

  const restore = useCallback(async (version) => {
    const ok = await confirm({
      title: `Restore version ${version}?`,
      message: 'The selected version will be copied into the draft. Workers continue to see the currently-published version until you Publish.',
      confirmLabel: 'Restore to draft',
    });
    if (!ok) return;
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/${layoutId}/versions/${version}/restore`, {}, { headers: authHeaders(token) });
      onRestored && onRestored();
    } catch (e) { setError(e?.response?.data?.detail || 'Restore failed'); }
  }, [token, workspaceId, layoutId, onRestored, confirm]);

  return (
    <div style={{ padding: '20px', overflow: 'auto' }}>
      <h3 style={{ marginTop: 0 }}>Version history</h3>
      <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
        A new version is written every time you Publish. Older versions are pruned past the workspace cap; restore copies a version back into the draft for review before re-publishing.
      </p>
      {error && <div style={styles.banner('#ef4444')}>{error}</div>}
      {loading && <div>Loading…</div>}
      {!loading && versions.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>This layout has never been published.</div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <tbody>
          {versions.map(v => (
            <tr key={v.id}>
              <td style={{ padding: '8px', borderBottom: '1px solid var(--border-color)', width: '60px' }}>v{v.version}</td>
              <td style={{ padding: '8px', borderBottom: '1px solid var(--border-color)' }}>
                <div>{v.change_note || <em style={{ color: 'var(--text-secondary)' }}>no note</em>}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>by user {v.published_by} · {v.published_at && new Date(v.published_at).toLocaleString()}</div>
              </td>
              <td style={{ padding: '8px', borderBottom: '1px solid var(--border-color)', width: '120px' }}>
                {!readOnly && (
                  <button onClick={() => restore(v.version)} style={styles.btnSecondary}>Restore to draft</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {confirmDialog}
    </div>
  );
}
